import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Document } from '@contentful/rich-text-types';
import { useRouter } from 'next/router';
import { Box } from '@zoopla-eng/zds-components/box';
import { ButtonLink } from '@zoopla-eng/zds-components/button';
import { CmsImage } from '@zoopla/cms/image';
import { trackSegmentAndGAEvent } from '@zoopla/tracking';
import ImagePlaceholder from '../assets/image-placeholder.svg';
import {
    bannerContentClass,
    imageClass,
    bannerTitleStyle,
    bannerContainerStyle,
    pictureClass
} from './banner.styles.css';
import { BANNER_RENDER_OPTIONS } from './render-options';
import type { EventTracking } from '../graphql.generated';
import { fontGroups } from '@zoopla-eng/zds-components/themes';
import { Stack } from '@zoopla-eng/zds-components/stack';

export type BannerButton = {
    label: string;
    url: string;
    category: 'primary' | 'secondary' | 'tertiary';
    eventTracking?: EventTracking;
};

export type BannerProps = {
    /**
     * defaults to `bottom`
     */
    imagePosition?: 'top' | 'bottom';
    imageSrc: string;
    imageAlt?: string;

    /**
     * defaults to `false`. `gridColumn` will overwrite.
     */
    fw?: boolean;

    title: string;
    body?: Document;

    /**
     * defaults to `brandPrimaryT4` which is `purple`
     */
    bgColor?: 'purple' | 'white' | 'green' | 'blue' | 'maroon' | 'orange';

    buttons?: BannerButton[];
};

function trackingClick(trackingData: EventTracking, page: string) {
    const { eventCategory, eventAction, segmentEventName } = trackingData;

    const GAEvent = {
        eventLabel: trackingData.eventLabel ?? undefined,
        eventCategory,
        eventAction
    };

    return trackSegmentAndGAEvent(segmentEventName, GAEvent, page);
}

export const Banner = ({
    imagePosition = 'bottom',
    fw = false,
    imageSrc,
    imageAlt = '',
    title,
    body,
    bgColor,
    buttons
}: BannerProps) => {
    const router = useRouter();

    const buttonsContent =
        buttons &&
        buttons.map((button) => {
            const { category, label, url, eventTracking } = button;
            const onButtonClick = eventTracking
                ? () => {
                      trackingClick(eventTracking, `${router.pathname}/`);
                  }
                : undefined;

            return (
                <ButtonLink
                    key={label}
                    category={category}
                    href={url}
                    onClick={onButtonClick}
                >
                    {label}
                </ButtonLink>
            );
        });

    return (
        <Box
            fw
            rowGap="S400"
            columnGap="S400"
            className={bannerContainerStyle({ bgColor, imagePosition })}
        >
            <Stack
                flexDirection="column"
                flexWrap="nowrap"
                justifyContent="space-between"
                rowGap={0}
                columnGap="S500"
                bpM100={{
                    flexDirection: 'row'
                }}
            >
                <Box
                    flex={1}
                    fw
                    alignItems="center"
                    display="flex"
                    columnGap="S400"
                >
                    <Box
                        className={bannerContentClass}
                        display="flex"
                        rowGap="S400"
                        flexDirection="column"
                    >
                        <h2
                            className={`${bannerTitleStyle} ${fontGroups.plate}`}
                        >
                            {title}
                        </h2>
                        {body
                            ? documentToReactComponents(
                                  body,
                                  BANNER_RENDER_OPTIONS
                              )
                            : null}
                        {buttons ? (
                            <Box
                                display="flex"
                                flexWrap="wrap"
                                columnGap="S500"
                                rowGap="S500"
                            >
                                {buttonsContent}
                            </Box>
                        ) : null}
                    </Box>
                </Box>
                <Box flex={1} display="flex" alignItems="flex-end">
                    <CmsImage
                        imgClassName={imageClass}
                        pictureClassName={pictureClass}
                        src={imageSrc}
                        alt={imageAlt}
                        sizes="(min-width: 768px) 50vw, 100vw"
                        onError={(e) =>
                            (e.currentTarget.src = ImagePlaceholder)
                        }
                    />
                </Box>
            </Stack>
        </Box>
    );
};
